import React, { useRef } from 'react';
import { Search, Filter, X } from 'lucide-react';
import { Input, Select, Button } from './';
import DeviceAutocomplete from '../DeviceAutocomplete';

const SearchFilterBar = ({
  searchValue,
  onSearchChange,
  onSearchSubmit,
  searchPlaceholder = 'Search...',
  filters = [],
  onFilterChange,
  onSearch,
  className = '',
  // Unified autocomplete props
  useAutocomplete = false, // Enable autocomplete search
  autocompleteType = 'devices', // 'devices' | 'products'
  onAutocompleteSelect = null, // Unified callback for item selection
  // Legacy props for backward compatibility
  useDeviceAutocomplete = false,
  useProductAutocomplete = false,
  onDeviceSelect = null,
  onProductSelect = null,
  // Device-specific props
  productId = null, // Optional: filter devices by product ID (only for autocompleteType='devices')
  includeAllStatuses = false, // Optional: include retired/donated/trashed devices in suggestions
  onSearchClear = null, // Optional: callback when search is cleared via X button
  ...props
}) => {
  const searchInputRef = useRef(null);
  // Determine if autocomplete should be used (support both new and legacy props)
  const shouldUseAutocomplete = useAutocomplete || useDeviceAutocomplete || useProductAutocomplete;
  
  // Determine the search type (support both new and legacy props)
  let effectiveSearchType = autocompleteType;
  if (useProductAutocomplete) {
    effectiveSearchType = 'products';
  } else if (useDeviceAutocomplete) {
    effectiveSearchType = 'devices';
  }
  
  // Determine the callback (support both new and legacy props)
  const effectiveCallback = onAutocompleteSelect || onDeviceSelect || onProductSelect;

  const handleClear = () => {
    if (onSearchChange) onSearchChange('');
    if (onSearchClear) onSearchClear();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && onSearchSubmit) {
      onSearchSubmit();
    }
  };

  const handleItemSelect = (item) => {
    if (item && onSearchChange) {
      // Determine the value to set based on search type and item structure
      let searchValue;
      if (effectiveSearchType === 'products') {
        searchValue = item.name;
      } else {
        // For devices, prefer serial_number, fallback to product_name or name
        searchValue = item.serial_number || item.product_name || item.name;
      }
      onSearchChange(searchValue);
    }
    
    // If parent provided a callback, let it control search to avoid race conditions
    if (effectiveCallback) {
      effectiveCallback(item);
      return;
    }
    
    // Otherwise, trigger search automatically when item is selected
    if (onSearchSubmit) {
      // Small delay to ensure state is updated
      setTimeout(() => {
        onSearchSubmit();
      }, 100);
    }
  };

  return (
    <div className={`bg-white p-3 rounded-lg shadow mb-3 ${className}`} {...props}>
      <div className="flex flex-col md:flex-row gap-3">
        {/* Search Input */}
        <div className="flex-1 relative overflow-visible">
          {shouldUseAutocomplete ? (
            <DeviceAutocomplete
              value={searchValue}
              onChange={onSearchChange}
              onDeviceSelect={handleItemSelect}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && onSearchSubmit) {
                  onSearchSubmit();
                }
              }}
              placeholder={searchPlaceholder}
              required={false}
              disabled={false}
              searchType={effectiveSearchType}
              productId={productId}
              includeAllStatuses={includeAllStatuses}
              onClear={handleClear}
              size="sm"
            />
          ) : (
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
              <Input
                ref={searchInputRef}
                value={searchValue}
                onChange={(e) => onSearchChange && onSearchChange(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={searchPlaceholder}
                size="sm"
                className={`pl-8 ${searchValue ? 'pr-8' : ''}`}
              />
              {searchValue && (
                <button
                  type="button"
                  onClick={() => {
                    handleClear();
                    searchInputRef.current?.focus();
                  }}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
                  title="Clear"
                >
                  <X size={16} />
                </button>
              )}
            </div>
          )}
        </div>

        {/* Filters */}
        <div className="flex items-center gap-2">
          {filters.length > 0 && (
            <Filter className="text-gray-400" size={16} />
          )}
          
          {filters.map((filter, index) => (
            <Select
              key={index}
              value={filter.value}
              onChange={(e) => onFilterChange && onFilterChange(filter.key, e.target.value)}
              options={filter.options}
              placeholder={filter.placeholder}
              size="sm"
              className="min-w-32"
            />
          ))}

          {/* Search Button */}
          {onSearch && (
            <Button
              variant="secondary"
              size="sm"
              onClick={onSearch}
              icon={Search}
            >
              Search
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SearchFilterBar;
